import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { PricingCategory } from "./PricingCategoryTabs";

const dayNames = ["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"];

const presetColors = [
  "#3b82f6", "#ef4444", "#f59e0b", "#10b981",
  "#8b5cf6", "#ec4899", "#06b6d4", "#f97316",
];

export interface PricingFormData {
  name: string;
  start_time: string;
  end_time: string;
  price_per_hour: number;
  is_peak: boolean;
  day_of_week: number[];
  color: string;
  category: string;
}

const emptyForm: PricingFormData = {
  name: "", start_time: "06:00", end_time: "22:00", price_per_hour: 0,
  is_peak: false, day_of_week: [1, 2, 3, 4, 5], color: "#3b82f6", category: "standard",
};

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initial?: Partial<PricingFormData> | null;
  editId?: string | null;
  categories: PricingCategory[];
  onSave: (data: PricingFormData) => void;
  saving?: boolean;
}

export default function PricingRuleDialog({ open, onOpenChange, initial, editId, categories, onSave, saving }: Props) {
  const [form, setForm] = useState<PricingFormData>(emptyForm);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...emptyForm, ...initial } : emptyForm);
    }
  }, [open, initial]);

  const toggleDay = (day: number) => {
    setForm((f) => ({
      ...f,
      day_of_week: f.day_of_week.includes(day) ? f.day_of_week.filter((d) => d !== day) : [...f.day_of_week, day].sort(),
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editId ? "Redigera prisregel" : "Ny prisregel"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Namn</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="T.ex. Peak vardagar" />
          </div>

          <div>
            <Label>Kategori</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Färg</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {presetColors.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm({ ...form, color: c })}
                  className="h-7 w-7 rounded-full border-2 transition-all"
                  style={{ backgroundColor: c, borderColor: form.color === c ? "hsl(var(--foreground))" : "transparent" }}
                />
              ))}
              <Input
                type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="h-7 w-10 p-0.5 cursor-pointer"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <div className="flex-1">
              <Label>Från</Label>
              <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
            </div>
            <div className="flex-1">
              <Label>Till</Label>
              <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
            </div>
          </div>

          <div>
            <Label>Pris/timme (kr)</Label>
            <NumberInput value={form.price_per_hour} onValueChange={(v) => setForm({ ...form, price_per_hour: v })} />
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={form.is_peak} onCheckedChange={(v) => setForm({ ...form, is_peak: v })} />
            <Label>Peak-tid</Label>
          </div>

          <div>
            <Label>Veckodagar</Label>
            <div className="flex gap-2 mt-1">
              {dayNames.map((name, i) => (
                <label key={i} className="flex items-center gap-1 text-xs">
                  <Checkbox checked={form.day_of_week.includes(i + 1)} onCheckedChange={() => toggleDay(i + 1)} />
                  {name}
                </label>
              ))}
            </div>
          </div>

          <Button className="w-full" onClick={() => onSave(form)} disabled={!form.name || saving}>
            {saving ? "Sparar…" : "Spara"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
